import styled from "@emotion/styled";
import { tokens } from "../../../design-system/tokens";

interface ButtonStyledProps {
  $variant: "primary" | "secondary" | "outline" | "ghost" | "danger";
  $size: "sm" | "md" | "lg" | "squareSm";
  $fullWidth: boolean;
  $loading: boolean;
}

const getVariantStyles = (variant: ButtonStyledProps["$variant"]) => {
  switch (variant) {
    case "primary":
      return `
        background: ${tokens.colors.blue[900]};
        color: ${tokens.colors.basics[0]};
        border: 1px solid ${tokens.colors.blue[900]};

        &:hover:not(:disabled) {
          background: ${tokens.colors.gradient.primary};
          border-color: ${tokens.colors.basics[0]};
          transform: translateY(-1px);
          box-shadow: ${tokens.shadows.md};
        }

        &:active:not(:disabled) {
          transform: translateY(0);
          box-shadow: ${tokens.shadows.sm};
        }
      `;
    case "secondary":
      return `
        background: ${tokens.colors.secondary[500]};
        color: ${tokens.colors.basics[0]};
        border: 1px solid ${tokens.colors.secondary[500]};

        &:hover:not(:disabled) {
          background: ${tokens.colors.secondary[500]};
          border-color: ${tokens.colors.secondary[500]};
        }
      `;
    case "outline":
      return `
        background: transparent;
        color: ${tokens.colors.blue[900]};
        border: 1px solid ${tokens.colors.blue[900]};

        &:hover:not(:disabled) {
          background: ${tokens.colors.blue[100]};
          color: ${tokens.colors.blue[800]};
        }
      `;
    case "ghost":
      return `
        background: transparent;
        color: ${tokens.colors.blue[900]};
        border: 1px solid transparent;

        &:hover:not(:disabled) {
          background: ${tokens.colors.blue[100]};
          color: ${tokens.colors.blue[800]};
        }
      `;
    case "danger":
      return `
        background: ${tokens.colors.semantic.error};
        color: ${tokens.colors.basics[0]};
        border: 1px solid ${tokens.colors.semantic.error};

        &:hover:not(:disabled) {
          background: #dc2626;
          border-color: #dc2626;
        }
      `;
    default:
      return "";
  }
};

const getSizeStyles = (size: ButtonStyledProps["$size"]) => {
  switch (size) {
    case "squareSm":
      return `
        padding: ${tokens.spacing.sm};
        border-radius: ${tokens.borderRadius.lg};
      `;
    case "sm":
      return `
        padding: ${tokens.spacing.xs} ${tokens.spacing.sm};
        font-size: ${tokens.typography.fontSize.xs};
        line-height: ${tokens.typography.lineHeight.tight};
      `;
    case "md":
      return `
        padding: ${tokens.spacing.sm} ${tokens.spacing.md};
        font-size: ${tokens.typography.fontSize.sm};
        line-height: ${tokens.typography.lineHeight.normal};
      `;
    case "lg":
      return `
        padding: ${tokens.spacing.md} ${tokens.spacing.lg};
        font-size: ${tokens.typography.fontSize.md};
        line-height: ${tokens.typography.lineHeight.normal};
        min-width: 150px;
      `;
    default:
      return "";
  }
};

export const ButtonStyled = styled.button<ButtonStyledProps>`
  /* Base styles */
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: ${tokens.typography.fontFamily.primary};
  font-weight: ${tokens.typography.fontWeight.semibold};
  text-align: center;
  border-radius: ${tokens.borderRadius.lg};
  cursor: pointer;
  gap: ${tokens.spacing.sm};
  transition: ${tokens.transitions.normal};
  user-select: none;
  outline: none;
  text-decoration: none;

  /* Evita el gap y la transparencia del fondo en foco */
  &:focus {
    outline: none;
  }

  /* opcional: elimina el highlight azul en móviles */
  -webkit-tap-highlight-color: transparent;

  /* Size styles */
  ${(props) => getSizeStyles(props.$size)}

  /* Variant styles */
  ${(props) => getVariantStyles(props.$variant)}

  /* Full width */
  ${(props) => props.$fullWidth && "width: 100%;"}

  /* Loading state */
  ${(props) =>
    props.$loading &&
    `
    cursor: not-allowed;
    opacity: 0.7;
  `}

  /* Disabled state */
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    background: ${tokens.colors.basics[400]};
    color: ${tokens.colors.basics[1100]};
    border-color: ${tokens.colors.basics[400]};

    &:hover {
      transform: none;
      box-shadow: none;
    }
  }
`;
